// The file contents for the current environment will overwrite these during build.
// The build system defaults to the dev environment which uses `environment.ts`, but if you do
// `ng build --env=prod` then `environment.prod.ts` will be used instead.
// The list of which env maps to which file can be found in `.angular-cli.json`.

import { BehaviorSubject } from 'rxjs/BehaviorSubject';

const noneBorder = {
  color: '#fff',
  width: {
    value: 0,
    unit: '%'
  }
};

const classicWhite = '#fff';
const classicBlack = '#000';

const title = new BehaviorSubject('ZÜRICH');
const subTitle = new BehaviorSubject('SWITZERLAND');
const description = new BehaviorSubject('47.377°N / 8.542°E');
const fontFamily = new BehaviorSubject('Roboto');

function createClassicBorders(color1: string, color2: string) {
  return [
    {
      background: color1,
      position: {
        top: { value: 0, unit: 'mh' },
        left: { value: 0, unit: 'mh' },
        bottom: { value: 0, unit: 'mh' },
        right: { value: 0, unit: 'mh' }
      },
      zIndex: 100
    },
    {
      background: color2,
      position: {
        top: { value: 0.03, unit: 'mh' },
        left: { value: 0.03, unit: 'mh' },
        bottom: { value: 0.03, unit: 'mh' },
        right: { value: 0.03, unit: 'mh' }
      },
      zIndex: 100
    },
    {
      background: color1,
      position: {
        top: { value: 0.034, unit: 'mh' },
        left: { value: 0.034, unit: 'mh' },
        bottom: { value: 0.034, unit: 'mh' },
        right: { value: 0.034, unit: 'mh' }
      },
      zIndex: 100
    },
    {
      background: color2,
      position: {
        top: { value: 0.038, unit: 'mh' },
        left: { value: 0.038, unit: 'mh' },
        bottom: { value: 0.038, unit: 'mh' },
        right: { value: 0.038, unit: 'mh' }
      },
      zIndex: 100
    },
    {
      background: color1,
      position: {
        top: { value: 0.0446, unit: 'mh' },
        left: { value: 0.0446, unit: 'mh' },
        bottom: { value: 0.0446, unit: 'mh' },
        right: { value: 0.0446, unit: 'mh' }
      },
      zIndex: 100
    }
  ];
}

function createClassicTexts(color: string) {
  return {
    title: {
      name: 'Title',
      value: title,
      font: {
        family: fontFamily,
        weight: 400,
        spacing: 'normal',
        style: 'normal'
      },
      color: color,
      size: { value: 0.05, unit: 'mh' },
      positionTop: '78%'
    },
    subtitle: {
      name: 'Subtitle',
      value: subTitle,
      font: {
        family: fontFamily,
        weight: 100,
        spacing: 'normal',
        style: 'normal'
      },
      color: color,
      size: { value: 0.03, unit: 'mh' },
      positionTop: '85%'
    },
    description: {
      name: 'Description',
      value: description,
      font: {
        family: fontFamily,
        weight: 100,
        spacing: 'normal',
        style: 'normal'
      },
      color: color,
      size: { value: 0.025, unit: 'mh' },
      positionTop: '90%'
    }
  };
}

function createClassicLines(color: string) {
  return [
    // left line
    {
      color: color,
      border: noneBorder,
      top: {
        value: 0.8675,
        unit: '%'
      },
      marginLeft: '0',
      marginRight: '1em',
      height: {
        value: '0.003',
        unit: 'mh'
      },
      right: {
        ref: {
          leftOf: 'text-Subtitle'
        },
        value: 0,
        unit: 'px'
      },
      left: {
        value: 0,
        unit: 'auto'
      },
      width: {
        value: 0.05,
        unit: '%'
      }
    },
    // right line
    {
      color: color,
      border: noneBorder,
      top: {
        value: 0.8675,
        unit: '%'
      },
      marginLeft: '1em',
      marginRight: '0',
      height: {
        value: '0.003',
        unit: 'mh'
      },
      right: {
        value: 0,
        unit: 'px'
      },
      left: {
        ref: {
          rightOf: 'text-Subtitle'
        },
        value: 0,
        unit: 'px'
      },
      width: {
        value: 0.05,
        unit: '%'
      }
    }
  ];
}

const twilightBlack = '#000';
const twilightOrange = '#bb7844';

const classicGraniteWhite = '#d5d5d5';
const classicGraniteBlack = '#343331';

const classicBorders = createClassicBorders(classicWhite, classicBlack);
const classicLines = createClassicLines(classicBlack);
const classicTexts = createClassicTexts(classicBlack);

const classicGraniteBorders = createClassicBorders(classicGraniteBlack, classicGraniteWhite);
const classicGraniteLines = createClassicLines(classicGraniteWhite);
const classicGraniteTexts = createClassicTexts(classicGraniteWhite);

// Chrome
const classicChromeWhite = '#ffffff';
const classicChromeBlack = '#2c404b';

const classicChromeBorders = createClassicBorders(classicChromeBlack, classicChromeWhite);
const classicChromeLines = createClassicLines(classicChromeWhite);
const classicChromeTexts = createClassicTexts(classicChromeWhite);

// Classic Black
const classicBlackBorder = '#ffffff';
const classicBlackBackground = '#000';

const classicBlackBorders = createClassicBorders(classicBlackBackground, classicBlackBorder);
const classicBlackLines = createClassicLines(classicBlackBorder);
const classicBlackTexts = createClassicTexts(classicBlackBorder);

// Twilight Neon
const twilightNeonBorder = '#2145f0';
const twilightNeonBackground = '#000';

const twilightNeonBorders = createClassicBorders(twilightNeonBackground, twilightNeonBorder);
const twilightNeonLines = createClassicLines(twilightNeonBorder);
const twilightNeonTexts = createClassicTexts(twilightNeonBorder);

// Twilight Red
const twilightRedBorder = '#be0303';
const twilightRedBackground = '#000';

const twilightRedBorders = createClassicBorders(twilightRedBackground, twilightRedBorder);
const twilightRedLines = createClassicLines(twilightRedBorder);
const twilightRedTexts = createClassicTexts(twilightRedBorder);

// Blueprint
const BlueprintBorder = '#ffffff';
const BlueprintDeepBackground = '#001484';
const BlueprintBackground = '#435cb1';

const blueprintBorders = createClassicBorders(BlueprintBackground, BlueprintBorder);
const blueprintDeepBorders = createClassicBorders(BlueprintDeepBackground, BlueprintBorder);
const blueprintDeepLines = createClassicLines(BlueprintBorder);
const blueprintDeepTexts = createClassicTexts(BlueprintBorder);

// Blueprint Purple
const BlueprintPurpleBorder = '#ffffff';
const BlueprintPurpleBackground = '#800080';

const blueprintPurpleBorders = createClassicBorders(BlueprintPurpleBackground, BlueprintPurpleBorder);
const blueprintPurpleLines = createClassicLines(BlueprintPurpleBorder);
const blueprintPurpleTexts = createClassicTexts(BlueprintPurpleBorder);

// Blueprint RoyalBlue
const BlueprintRoyalBlueBorder = '#ffffff';
const BlueprintRoyalBlueBackground = '#49638a';

const blueprintRoyalBlueBorders = createClassicBorders(BlueprintRoyalBlueBackground, BlueprintRoyalBlueBorder);
const blueprintRoyalBlueLines = createClassicLines(BlueprintRoyalBlueBorder);
const blueprintRoyalBlueTexts = createClassicTexts(BlueprintRoyalBlueBorder);

// Blueprint Coral
const BlueprintCoralBorder = '#ffffff';
const BlueprintCoralBackground = '#faac6c';

const blueprintCoralBorders = createClassicBorders(BlueprintCoralBackground, BlueprintCoralBorder);
const blueprintCoralLines = createClassicLines(BlueprintCoralBorder);
const blueprintCoralTexts = createClassicTexts(BlueprintCoralBorder);

// Blueprint White
const BlueprintWhiteBorder = '#000000';
const BlueprintWhiteBackground = '#ffffff';

const blueprintWhiteBorders = createClassicBorders(BlueprintWhiteBackground, BlueprintWhiteBorder);
const blueprintWhiteLines = createClassicLines(BlueprintWhiteBorder);
const blueprintWhiteTexts = createClassicTexts(BlueprintWhiteBorder);

// Blueprint Surfblue
const BlueprintSurfblueBorder = '#ffffff';
const BlueprintSurfblueBackground = '#3d89c1';

const blueprintSurfblueBorders = createClassicBorders(BlueprintSurfblueBackground, BlueprintSurfblueBorder);
const blueprintSurfblueLines = createClassicLines(BlueprintSurfblueBorder);
const blueprintSurfblueTexts = createClassicTexts(BlueprintSurfblueBorder);

// Blueprint Olive
const BlueprintOliveBorder = '#ffffff';
const BlueprintOliveBackground = '#8a977e';

const blueprintOliveBorders = createClassicBorders(BlueprintOliveBackground, BlueprintOliveBorder);
const blueprintOliveLines = createClassicLines(BlueprintOliveBorder);
const blueprintOliveTexts = createClassicTexts(BlueprintOliveBorder);

// Blueprint Yellow
const BlueprintYellowBorder = '#ffffff';
const BlueprintYellowBackground = '#f4c20d';

const blueprintYellowBorders = createClassicBorders(BlueprintYellowBackground, BlueprintYellowBorder);
const blueprintYellowLines = createClassicLines(BlueprintYellowBorder);
const blueprintYellowTexts = createClassicTexts(BlueprintYellowBorder);

// Blueprint Dark
const BlueprintDarkBorder = '#ffffff';
const BlueprintDarkBackground = '#2a313f';

const blueprintDarkBorders = createClassicBorders(BlueprintDarkBackground, BlueprintDarkBorder);
const blueprintDarkLines = createClassicLines(BlueprintDarkBorder);
const blueprintDarkTexts = createClassicTexts(BlueprintDarkBorder);

// Blueprint Deep
const BlueprintDeepBorder = '#ffffff';

// Blueprint Pink
const BlueprintPinkBorder = '#ffffff';
const BlueprintPinkBackground = '#ffc0cb';

const blueprintPinkBorders = createClassicBorders(BlueprintPinkBackground, BlueprintPinkBorder);
const blueprintPinkLines = createClassicLines(BlueprintPinkBorder);
const blueprintPinkTexts = createClassicTexts(BlueprintPinkBorder);

// Blueprint Charcoal
const BlueprintCharcoalBorder = '#ffffff';
const BlueprintCharcoalBackground = '#242424';

const blueprintCharcoalBorders = createClassicBorders(BlueprintCharcoalBackground, BlueprintCharcoalBorder);
const blueprintCharcoalLines = createClassicLines(BlueprintCharcoalBorder);
const blueprintCharcoalTexts = createClassicTexts(BlueprintCharcoalBorder);

// Blueprint Black
const BlueprintBlackBorder = '#ffffff';
const BlueprintBlackBackground = '#000000';

const blueprintBlackBorders = createClassicBorders(BlueprintBlackBackground, BlueprintBlackBorder);
const blueprintBlackLines = createClassicLines(BlueprintBlackBorder);
const blueprintBlackTexts = createClassicTexts(BlueprintBlackBorder);

// Blueprint PurpleDark
const BlueprintPurpleDarkBorder = '#ffffff';
const BlueprintPurpleDarkBackground = '#320032';

const blueprintPurpleDarkBorders = createClassicBorders(BlueprintPurpleDarkBackground, BlueprintPurpleDarkBorder);
const blueprintPurpleDarkLines = createClassicLines(BlueprintPurpleDarkBorder);
const blueprintPurpleDarkTexts = createClassicTexts(BlueprintPurpleDarkBorder);

// Blueprint Cactus
const BlueprintCactusBorder = '#ffffff';
const BlueprintCactusBackground = '#708d8d';

const blueprintCactusBorders = createClassicBorders(BlueprintCactusBackground, BlueprintCactusBorder);
const blueprintCactusLines = createClassicLines(BlueprintCactusBorder);
const blueprintCactusTexts = createClassicTexts(BlueprintCactusBorder);

// Blueprint HazelnutIceCream
const BlueprintHazelnutIceCreamBorder = '#c6a381';
const BlueprintHazelnutIceCreamBackground = '#fefbea';

const blueprintHazelnutIceCreamBorders = createClassicBorders(
  BlueprintHazelnutIceCreamBackground,
  BlueprintHazelnutIceCreamBorder
);
const blueprintHazelnutIceCreamLines = createClassicLines(BlueprintHazelnutIceCreamBorder);
const blueprintHazelnutIceCreamTexts = createClassicTexts(BlueprintHazelnutIceCreamBorder);

// Blueprint Green
const BlueprintGreenBorder = '#ffffff';
const BlueprintGreenBackground = '#143725';

const blueprintGreenBorders = createClassicBorders(BlueprintGreenBackground, BlueprintGreenBorder);
const blueprintGreenLines = createClassicLines(BlueprintGreenBorder);
const blueprintGreenTexts = createClassicTexts(BlueprintGreenBorder);

// Blueprint ChocolateIceCream
const BlueprintChocolateIceCreamBorder = '#30160f';
const BlueprintChocolateIceCreamBackground = '#7d5745';

const blueprintChocolateIceCreamBorders = createClassicBorders(
  BlueprintChocolateIceCreamBackground,
  BlueprintChocolateIceCreamBorder
);
const blueprintChocolateIceCreamLines = createClassicLines(BlueprintChocolateIceCreamBorder);
const blueprintChocolateIceCreamTexts = createClassicTexts(BlueprintChocolateIceCreamBorder);

// Blueprint Grapefruit
const BlueprintGrapefruitBorder = '#ffffff';
const BlueprintGrapefruitBackground = '#fec3b2';

const blueprintGrapefruitBorders = createClassicBorders(BlueprintGrapefruitBackground, BlueprintGrapefruitBorder);
const blueprintGrapefruitLines = createClassicLines(BlueprintGrapefruitBorder);
const blueprintGrapefruitTexts = createClassicTexts(BlueprintGrapefruitBorder);

// Blueprint Orange
const BlueprintOrangeBorder = '#ffffff';
const BlueprintOrangeBackground = '#e55300';

const blueprintOrangeBorders = createClassicBorders(BlueprintOrangeBackground, BlueprintOrangeBorder);
const blueprintOrangeLines = createClassicLines(BlueprintOrangeBorder);
const blueprintOrangeTexts = createClassicTexts(BlueprintOrangeBorder);

// Blueprint Red
const BlueprintRedBorder = '#ffffff';
const BlueprintRedBackground = '#840000';

const blueprintRedBorders = createClassicBorders(BlueprintRedBackground, BlueprintRedBorder);
const blueprintRedLines = createClassicLines(BlueprintRedBorder);
const blueprintRedTexts = createClassicTexts(BlueprintRedBorder);

const twilightBorders = [
  // top
  {
    background: `linear-gradient(
              to bottom,
              rgba(0, 0, 0, 1) 0%,
              rgba(0, 0, 0, .9684) 25%,
              rgba(0, 0, 0, .9) 50%,
              rgba(0, 0, 0, .5772) 75%,
              rgba(0, 0, 0, 0) 100%
            )`,
    position: {
      top: { value: 0, unit: 'mh' },
      left: { value: 0, unit: 'mh' },
      bottom: { value: 0.94, unit: 'mh' },
      right: { value: 0, unit: 'mh' }
    },
    zIndex: 105
  },
  // right
  {
    background: `linear-gradient(
              to right,
              rgba(0, 0, 0, 0) 0%,
              rgba(0, 0, 0, .5772) 25%,
              rgba(0, 0, 0, .9) 50%,
              rgba(0, 0, 0, .9684) 75%,
              rgba(0, 0, 0, 1) 100%
            )`,
    position: {
      top: { value: 0, unit: 'px' },
      bottom: { value: 0, unit: 'px' },
      right: { value: 0, unit: 'px' }
    },
    width: { value: 0.06, unit: 'mh' },
    zIndex: 105
  },
  // bottom
  {
    background: `linear-gradient(
              to bottom,
              rgba(0, 0, 0, 0) 0%,
              rgba(0, 0, 0, .5772) 20.38%,
              rgba(0, 0, 0, .9) 40.75%,
              rgba(0, 0, 0, .9684) 61.13%,
              rgba(0, 0, 0, 1) 81.5%,
              rgba(0, 0, 0, 1) 100%
            )`,
    position: {
      top: { value: 0.81, unit: '%' },
      left: { value: 0, unit: 'mh' },
      bottom: { value: 0, unit: 'mh' },
      right: { value: 0, unit: 'mh' }
    },
    zIndex: 105
  },
  // left
  {
    background: `linear-gradient(
              to right,
              rgba(0, 0, 0, 1) 0%,
              rgba(0, 0, 0, .9684) 25%,
              rgba(0, 0, 0, .9) 50%,
              rgba(0, 0, 0, .5772) 75%,
              rgba(0, 0, 0, 0) 100%
            )`,
    position: {
      top: { value: 0, unit: 'px' },
      left: { value: 0, unit: 'px' },
      bottom: { value: 0, unit: 'px' }
    },
    width: { value: 0.06, unit: 'mh' },
    zIndex: 105
  },
  {
    background: twilightBlack,
    position: {
      top: { value: 0, unit: 'mh' },
      left: { value: 0, unit: 'mh' },
      bottom: { value: 0, unit: 'mh' },
      right: { value: 0, unit: 'mh' }
    },
    zIndex: 100
  }
];

const twilightPanoramicBorders = JSON.parse(JSON.stringify(twilightBorders));
twilightPanoramicBorders.splice(3, 0, {
  background: `radial-gradient(
                at bottom,
                rgba(0, 0, 0, 1) 33%,
                rgba(0, 0, 0, .5) 66%,
                rgba(0, 0, 0, 0) 75%,
                rgba(0, 0, 0, 0) 100%
              )`,
  position: {
    top: { value: 0.8, unit: '%' },
    left: { value: 0.33, unit: '%' },
    bottom: { value: 0, unit: 'px' },
    right: { value: 0.33, unit: '%' }
  },
  zIndex: 105
});
twilightPanoramicBorders[2].position.top = { value: 0.94, unit: '%' };

const twilightTexts = {
  title: {
    name: 'Title',
    value: title,
    font: {
      family: fontFamily,
      weight: 700,
      spacing: '.75em',
      style: 'normal'
    },
    color: twilightOrange,
    size: {
      value: 0.03,
      unit: 'mh'
    },
    positionTop: '90%'
  },
  subtitle: {
    name: 'Subtitle',
    value: subTitle,
    font: {
      family: fontFamily,
      weight: 400,
      spacing: '1em',
      style: 'normal'
    },
    color: twilightOrange,
    size: {
      value: 0.015,
      unit: 'mh'
    },
    positionTop: '87%'
  },
  description: {
    name: 'Description',
    value: description,
    font: {
      family: fontFamily,
      weight: 400,
      spacing: '1em',
      style: 'normal'
    },
    color: twilightOrange,
    size: {
      value: 0.015,
      unit: 'mh'
    },
    positionTop: '95%'
  }
};

const twilightTextsSimple = {
  title: {
    name: 'Title',
    value: title,
    font: {
      family: fontFamily,
      weight: 400,
      spacing: '.75em',
      style: 'normal'
    },
    color: twilightOrange,
    size: {
      value: 0.03,
      unit: 'mh'
    },
    positionTop: '90%'
  }
};

const twilightLines = [
  {
    color: '#bb7844',
    border: noneBorder,
    top: {
      value: 0.895,
      unit: '%'
    },
    marginLeft: 'auto',
    marginRight: 'auto',
    height: {
      value: 1,
      unit: 'px'
    },
    right: {
      value: 0,
      unit: 'px'
    },
    left: {
      value: 0,
      unit: 'px'
    },
    width: {
      ref: {
        widthOf: ['text-Title', 'text-Subtitle', 'text-Description']
      },
      value: 1.2,
      unit: '%'
    }
  },
  {
    color: '#bb7844',
    border: noneBorder,
    top: {
      value: 0.94,
      unit: '%'
    },
    marginLeft: 'auto',
    marginRight: 'auto',
    height: {
      value: 1,
      unit: 'px'
    },
    right: {
      value: 0,
      unit: 'px'
    },
    left: {
      value: 0,
      unit: 'px'
    },
    width: {
      ref: {
        widthOf: ['text-Title', 'text-Subtitle', 'text-Description']
      },
      value: 1.2,
      unit: '%'
    }
  }
];

const classicIcons = {
  heart: {
    url: require('./assets/img/heart-classic.svg')
  },
  house: {
    url: require('./assets/img/house-classic.svg')
  },
  star: {
    url: require('./assets/img/star-classic.svg')
  },
  ring: {
    url: require('./assets/img/ring-classic.svg')
  },
  plane: {
    url: require('./assets/img/plane-classic.svg')
  },
  hat: {
    url: require('./assets/img/hat-classic.svg')
  },
  ski: {
    url: require('./assets/img/ski-classic.svg')
  }
};

const twilightIcons = {
  heart: {
    url: require('./assets/img/heart-twilight.svg')
  },
  house: {
    url: require('./assets/img/house-twilight.svg')
  },
  star: {
    url: require('./assets/img/star-twilight.svg')
  },
  ring: {
    url: require('./assets/img/ring-twilight.svg')
  },
  plane: {
    url: require('./assets/img/plane-twilight.svg')
  },
  hat: {
    url: require('./assets/img/hat-twilight.svg')
  }
};

const classicGraniteIcons = {
  heart: {
    url: require('./assets/img/heart-classic-granite.svg')
  },
  house: {
    url: require('./assets/img/house-classic-granite.svg')
  },
  star: {
    url: require('./assets/img/star-classic-granite.svg')
  },
  ring: {
    url: require('./assets/img/ring-classic-granite.svg')
  },
  plane: {
    url: require('./assets/img/plane-classic-granite.svg')
  },
  hat: {
    url: require('./assets/img/hat-classic-granite.svg')
  }
};

const classicChromeIcons = {
  heart: {
    url: require('./assets/img/heart-classic-chrome.svg')
  },
  house: {
    url: require('./assets/img/house-classic-chrome.svg')
  },
  star: {
    url: require('./assets/img/star-classic-chrome.svg')
  },
  ring: {
    url: require('./assets/img/ring-classic-chrome.svg')
  },
  plane: {
    url: require('./assets/img/plane-classic-chrome.svg')
  },
  hat: {
    url: require('./assets/img/hat-classic-chrome.svg')
  }
};

const TwilightNeonIconsWhite = {
  heart: {
    url: require('./assets/img/heart-twilight-neon.svg')
  },
  house: {
    url: require('./assets/img/house-twilight-neon.svg')
  },
  star: {
    url: require('./assets/img/star-twilight-neon.svg')
  },
  ring: {
    url: require('./assets/img/ring-twilight-neon.svg')
  },
  plane: {
    url: require('./assets/img/plane-twilight-neon.svg')
  },
  hat: {
    url: require('./assets/img/hat-twilight-neon.svg')
  }
};

const TwilightRedIconsWhite = {
  heart: {
    url: require('./assets/img/heart-twilight-red.svg')
  },
  house: {
    url: require('./assets/img/house-twilight-red.svg')
  },
  star: {
    url: require('./assets/img/star-twilight-red.svg')
  },
  ring: {
    url: require('./assets/img/ring-twilight-red.svg')
  },
  plane: {
    url: require('./assets/img/plane-twilight-red.svg')
  },
  hat: {
    url: require('./assets/img/hat-twilight-red.svg')
  }
};

const BlueprintIconsWhite = {
  heart: {
    url: require('./assets/img/heart-blueprint-deep.svg')
  },
  house: {
    url: require('./assets/img/house-blueprint-deep.svg')
  },
  star: {
    url: require('./assets/img/star-blueprint-deep.svg')
  },
  ring: {
    url: require('./assets/img/ring-blueprint-deep.svg')
  },
  plane: {
    url: require('./assets/img/plane-blueprint-deep.svg')
  },
  hat: {
    url: require('./assets/img/hat-blueprint-deep.svg')
  }
};

const BlueprintIconsHazelnutIceCream = {
  heart: {
    url: require('./assets/img/heart-blueprint-hazelnut-ice-cream.svg')
  },
  house: {
    url: require('./assets/img/house-blueprint-hazelnut-ice-cream.svg')
  },
  star: {
    url: require('./assets/img/star-blueprint-hazelnut-ice-cream.svg')
  },
  ring: {
    url: require('./assets/img/ring-blueprint-hazelnut-ice-cream.svg')
  },
  plane: {
    url: require('./assets/img/plane-blueprint-hazelnut-ice-cream.svg')
  },
  hat: {
    url: require('./assets/img/hat-blueprint-hazelnut-ice-cream.svg')
  }
};

const BlueprintIconsChocolateIceCream = {
  heart: {
    url: require('./assets/img/heart-blueprint-chocolate-ice-cream.svg')
  },
  house: {
    url: require('./assets/img/house-blueprint-chocolate-ice-cream.svg')
  },
  star: {
    url: require('./assets/img/star-blueprint-chocolate-ice-cream.svg')
  },
  ring: {
    url: require('./assets/img/ring-blueprint-chocolate-ice-cream.svg')
  },
  plane: {
    url: require('./assets/img/plane-blueprint-chocolate-ice-cream.svg')
  },
  hat: {
    url: require('./assets/img/hat-blueprint-chocolate-ice-cream.svg')
  }
};

function createStyle(borders, icons, lines, texts) {
  return {
    portrait: {
      classic: {
        borders,
        icons,
        lines,
        texts
      },
      'no-text': {
        borders,
        icons
      },
      'canvas-only': {
        icons
      }
    },
    landscape: {
      classic: {
        borders,
        icons,
        lines,
        texts
      },
      'no-text': {
        borders,
        icons
      },
      'canvas-only': {
        icons
      }
    },
    square: {
      classic: {
        borders,
        icons,
        lines,
        texts
      },
      'no-text': {
        borders,
        icons
      },
      'canvas-only': {
        icons
      }
    },
    panoramic: {
      classic: {
        borders,
        icons,
        lines,
        texts
      },
      'no-text': {
        borders,
        icons
      },
      'canvas-only': {
        icons
      }
    }
  };
}

export const environment = {
  production: false,
  version: require('../package.json').version,
  api: 'https://api.mapify-studio.com',
  thumbnailApi: 'https://api.mapify-studio.com',
  mapifierURL: 'https://editor.mapeo.ch',
  preview: {
    email: 'hello@mapeo.ch'
  },
  dataPrivacyPolicyURL: '',
  ga: {
    enabled: false,
    trackingId: 'UA-58047905-2',
    trackerConfig: { name: '', siteSpeedSampleRate: '100', cookieDomain: 'auto' }
  },
  googleAccessToken: 'AIzaSyDVEk713kJThyaYK8gtuYss4XlKrXhQNMQ',
  screen: { smartphone: 690, tablet: 995, desktop: 1440 },
  mapbox: {
    accessToken: 'pk.eyJ1IjoibWFwaWZ5LXN0dWRpbyIsImEiOiJjaXF5eXh3cXMwMDVjaHpuam15MXZ3bnU5In0.muqea7XrZoKpGxSf3Epiqg'
  },
  sentry: {
    enabled: false,
    dsn: 'https://ef63e5b487394469a93610154b451eed@sentry.io/214758'
  },
  mapify: {
    prices: {
      CHF: {
        ALUDIBOND120X40CM: 97.0,
        ALUDIBOND12X12INCH: 967.0,
        ALUDIBOND150X50CM: 183.0,
        ALUDIBOND180X60CM: 215.0,
        ALUDIBOND18X24INCH: 946.0,
        ALUDIBOND20X20INCH: 951.0,
        ALUDIBOND24X36INCH: 936.0,
        ALUDIBOND30X30CM: 967.0,
        ALUDIBOND47X16INCH: 97.0,
        ALUDIBOND50X50CM: 951.0,
        ALUDIBOND50X70CM: 946.0,
        ALUDIBOND59X20INCH: 183.0,
        ALUDIBOND61X91CM: 936.0,
        ALUDIBOND70X100CM: 925.0,
        ALUDIBOND71X24INCH: 215.0,
        CUSTOM10X10: 27.0,
        CUSTOM120X40: 74.0,
        CUSTOM12X12: 32.0,
        CUSTOM150X50: 85.0,
        CUSTOM180X60: 96.0,
        CUSTOM18X24: 53.0,
        CUSTOM20X20: 48.0,
        CUSTOM24X36: 63.0,
        CUSTOM25X25: 27.0,
        CUSTOM30X30: 32.0,
        CUSTOM47X16: 74.0,
        CUSTOM50X50: 48.0,
        CUSTOM50X70: 53.0,
        CUSTOM59X20: 85.0,
        CUSTOM61X91: 63.0,
        CUSTOM70X100: 74.0,
        CUSTOM71X24: 96.0,
        FRAMECLASSIC10X10WOODBLACK: 17.0,
        FRAMECLASSIC25X25WOODBLACK: 17.0,
        FRAMEGLOBAL12X12INCHWOODBLACK: 22.0,
        FRAMEGLOBAL12X12INCHWOODNATURAL: 22.0,
        FRAMEGLOBAL12X12INCHWOODWHITE: 22.0,
        FRAMEGLOBAL18X24INCHWOODBLACK: 43.0,
        FRAMEGLOBAL18X24INCHWOODNATURAL: 43.0,
        FRAMEGLOBAL18X24INCHWOODWHITE: 43.0,
        FRAMEGLOBAL20X20INCHWOODBLACK: 30.0,
        FRAMEGLOBAL20X20INCHWOODNATURAL: 30.0,
        FRAMEGLOBAL20X20INCHWOODWHITE: 30.0,
        FRAMEGLOBAL24X36INCHWOODBLACK: 60.0,
        FRAMEGLOBAL24X36INCHWOODNATURAL: 60.0,
        FRAMEGLOBAL24X36INCHWOODWHITE: 60.0,
        FRAMEGLOBAL30X30CMWOODBLACK: 22.0,
        FRAMEGLOBAL30X30CMWOODNATURAL: 22.0,
        FRAMEGLOBAL30X30CMWOODWHITE: 22.0,
        FRAMEGLOBAL50X50CMWOODBLACK: 33.0,
        FRAMEGLOBAL50X50CMWOODNATURAL: 33.0,
        FRAMEGLOBAL50X50CMWOODWHITE: 33.0,
        FRAMEGLOBAL50X70CMWOODBLACK: 43.0,
        FRAMEGLOBAL50X70CMWOODNATURAL: 43.0,
        FRAMEGLOBAL50X70CMWOODWHITE: 43.0,
        FRAMEGLOBAL61X91CMWOODBLACK: 60.0,
        FRAMEGLOBAL61X91CMWOODNATURAL: 60.0,
        FRAMEGLOBAL61X91CMWOODWHITE: 60.0,
        FRAMEGLOBAL70X100CMWOODBLACK: 64.0,
        FRAMEGLOBAL70X100CMWOODNATURAL: 64.0,
        FRAMEGLOBAL70X100CMWOODWHITE: 64.0,
        FRAMEPANORAMIC120X40CMWOODBLACK: 65.0,
        FRAMEPANORAMIC150X50CMWOODBLACK: 97.0,
        FRAMEPANORAMIC180X60CMWOODBLACK: 150.0,
        FRAMEPANORAMIC47X16INCHWOODBLACK: 65.0,
        FRAMEPANORAMIC59X20INCHWOODBLACK: 97.0,
        FRAMEPANORAMIC71X24INCHWOODBLACK: 150.0
      },
      EUR: {
        ALUDIBOND120X40CM: 90.0,
        ALUDIBOND12X12INCH: 19.0,
        ALUDIBOND150X50CM: 170.0,
        ALUDIBOND180X60CM: 200.0,
        ALUDIBOND18X24INCH: 30.0,
        ALUDIBOND20X20INCH: 24.0,
        ALUDIBOND24X36INCH: 30.0,
        ALUDIBOND30X30CM: 19.0,
        ALUDIBOND47X16INCH: 90.0,
        ALUDIBOND50X50CM: 24.0,
        ALUDIBOND50X70CM: 30.0,
        ALUDIBOND59X20INCH: 170.0,
        ALUDIBOND61X91CM: 30.0,
        ALUDIBOND70X100CM: 30.0,
        ALUDIBOND71X24INCH: 200.0,
        CUSTOM10X10: 25.0,
        CUSTOM120X40: 69.0,
        CUSTOM12X12: 30.0,
        CUSTOM150X50: 79.0,
        CUSTOM180X60: 89.0,
        CUSTOM18X24: 49.0,
        CUSTOM20X20: 45.0,
        CUSTOM24X36: 59.0,
        CUSTOM25X25: 25.0,
        CUSTOM30X30: 30.0,
        CUSTOM47X16: 69.0,
        CUSTOM50X50: 45.0,
        CUSTOM50X70: 49.0,
        CUSTOM59X20: 79.0,
        CUSTOM61X91: 59.0,
        CUSTOM70X100: 69.0,
        CUSTOM71X24: 89.0,
        FRAMECLASSIC10X10WOODBLACK: 16.0,
        FRAMECLASSIC25X25WOODBLACK: 16.0,
        FRAMEGLOBAL12X12INCHWOODBLACK: 20.0,
        FRAMEGLOBAL12X12INCHWOODNATURAL: 20.0,
        FRAMEGLOBAL12X12INCHWOODWHITE: 20.0,
        FRAMEGLOBAL18X24INCHWOODBLACK: 40.0,
        FRAMEGLOBAL18X24INCHWOODNATURAL: 40.0,
        FRAMEGLOBAL18X24INCHWOODWHITE: 40.0,
        FRAMEGLOBAL20X20INCHWOODBLACK: 28.0,
        FRAMEGLOBAL20X20INCHWOODNATURAL: 28.0,
        FRAMEGLOBAL20X20INCHWOODWHITE: 28.0,
        FRAMEGLOBAL24X36INCHWOODBLACK: 55.0,
        FRAMEGLOBAL24X36INCHWOODNATURAL: 55.0,
        FRAMEGLOBAL24X36INCHWOODWHITE: 55.0,
        FRAMEGLOBAL30X30CMWOODBLACK: 20.0,
        FRAMEGLOBAL30X30CMWOODNATURAL: 20.0,
        FRAMEGLOBAL30X30CMWOODWHITE: 20.0,
        FRAMEGLOBAL50X50CMWOODBLACK: 30.0,
        FRAMEGLOBAL50X50CMWOODNATURAL: 30.0,
        FRAMEGLOBAL50X50CMWOODWHITE: 30.0,
        FRAMEGLOBAL50X70CMWOODBLACK: 40.0,
        FRAMEGLOBAL50X70CMWOODNATURAL: 40.0,
        FRAMEGLOBAL50X70CMWOODWHITE: 40.0,
        FRAMEGLOBAL61X91CMWOODBLACK: 55.0,
        FRAMEGLOBAL61X91CMWOODNATURAL: 55.0,
        FRAMEGLOBAL61X91CMWOODWHITE: 55.0,
        FRAMEGLOBAL70X100CMWOODBLACK: 59.0,
        FRAMEGLOBAL70X100CMWOODNATURAL: 59.0,
        FRAMEGLOBAL70X100CMWOODWHITE: 59.0,
        FRAMEPANORAMIC120X40CMWOODBLACK: 60.0,
        FRAMEPANORAMIC150X50CMWOODBLACK: 90.0,
        FRAMEPANORAMIC180X60CMWOODBLACK: 140.0,
        FRAMEPANORAMIC47X16INCHWOODBLACK: 60.0,
        FRAMEPANORAMIC59X20INCHWOODBLACK: 90.0,
        FRAMEPANORAMIC71X24INCHWOODBLACK: 140.0
      },
      USD: {
        ALUDIBOND120X40CM: 110.0,
        ALUDIBOND12X12INCH: 20.0,
        ALUDIBOND150X50CM: 204.0,
        ALUDIBOND180X60CM: 244.0,
        ALUDIBOND18X24INCH: 30.0,
        ALUDIBOND20X20INCH: 29.0,
        ALUDIBOND24X36INCH: 30.0,
        ALUDIBOND30X30CM: 20.0,
        ALUDIBOND47X16INCH: 110.0,
        ALUDIBOND50X50CM: 29.0,
        ALUDIBOND50X70CM: 30.0,
        ALUDIBOND59X20INCH: 204.0,
        ALUDIBOND61X91CM: 30.0,
        ALUDIBOND70X100CM: 44.0,
        ALUDIBOND71X24INCH: 244.0,
        CUSTOM10X10: 29.0,
        CUSTOM120X40: 79.0,
        CUSTOM12X12: 35.0,
        CUSTOM150X50: 85.0,
        CUSTOM180X60: 95.0,
        CUSTOM18X24: 55.0,
        CUSTOM20X20: 50.0,
        CUSTOM24X36: 65.0,
        CUSTOM25X25: 29.0,
        CUSTOM30X30: 35.0,
        CUSTOM47X16: 79.0,
        CUSTOM50X50: 50.0,
        CUSTOM50X70: 55.0,
        CUSTOM59X20: 85.0,
        CUSTOM61X91: 65.0,
        CUSTOM70X100: 75.0,
        CUSTOM71X24: 95.0,
        FRAMECLASSIC10X10WOODBLACK: 19.0,
        FRAMECLASSIC25X25WOODBLACK: 19.0,
        FRAMEGLOBAL12X12INCHWOODBLACK: 21.0,
        FRAMEGLOBAL12X12INCHWOODNATURAL: 21.0,
        FRAMEGLOBAL12X12INCHWOODWHITE: 21.0,
        FRAMEGLOBAL18X24INCHWOODBLACK: 42.0,
        FRAMEGLOBAL18X24INCHWOODNATURAL: 42.0,
        FRAMEGLOBAL18X24INCHWOODWHITE: 42.0,
        FRAMEGLOBAL20X20INCHWOODBLACK: 29.0,
        FRAMEGLOBAL20X20INCHWOODNATURAL: 29.0,
        FRAMEGLOBAL20X20INCHWOODWHITE: 29.0,
        FRAMEGLOBAL24X36INCHWOODBLACK: 50.0,
        FRAMEGLOBAL24X36INCHWOODNATURAL: 58.0,
        FRAMEGLOBAL24X36INCHWOODWHITE: 58.0,
        FRAMEGLOBAL30X30CMWOODBLACK: 21.0,
        FRAMEGLOBAL30X30CMWOODNATURAL: 21.0,
        FRAMEGLOBAL30X30CMWOODWHITE: 21.0,
        FRAMEGLOBAL50X50CMWOODBLACK: 32.0,
        FRAMEGLOBAL50X50CMWOODNATURAL: 32.0,
        FRAMEGLOBAL50X50CMWOODWHITE: 32.0,
        FRAMEGLOBAL50X70CMWOODBLACK: 42.0,
        FRAMEGLOBAL50X70CMWOODNATURAL: 42.0,
        FRAMEGLOBAL50X70CMWOODWHITE: 42.0,
        FRAMEGLOBAL61X91CMWOODBLACK: 58.0,
        FRAMEGLOBAL61X91CMWOODNATURAL: 58.0,
        FRAMEGLOBAL61X91CMWOODWHITE: 58.0,
        FRAMEGLOBAL70X100CMWOODBLACK: 63.0,
        FRAMEGLOBAL70X100CMWOODNATURAL: 63.0,
        FRAMEGLOBAL70X100CMWOODWHITE: 63.0,
        FRAMEPANORAMIC120X40CMWOODBLACK: 70.0,
        FRAMEPANORAMIC150X50CMWOODBLACK: 114.0,
        FRAMEPANORAMIC180X60CMWOODBLACK: 174.0,
        FRAMEPANORAMIC47X16INCHWOODBLACK: 70.0,
        FRAMEPANORAMIC59X20INCHWOODBLACK: 114.0,
        FRAMEPANORAMIC71X24INCHWOODBLACK: 174.0
      },
      GBP: {
        ALUDIBOND120X40CM: 80.0,
        ALUDIBOND12X12INCH: 15.0,
        ALUDIBOND150X50CM: 145.0,
        ALUDIBOND180X60CM: 174.0,
        ALUDIBOND18X24INCH: 26.5,
        ALUDIBOND20X20INCH: 20.0,
        ALUDIBOND24X36INCH: 26.0,
        ALUDIBOND30X30CM: 15.0,
        ALUDIBOND47X16INCH: 80.0,
        ALUDIBOND50X50CM: 20.0,
        ALUDIBOND50X70CM: 26.5,
        ALUDIBOND59X20INCH: 145.0,
        ALUDIBOND61X91CM: 26.0,
        ALUDIBOND70X100CM: 26.0,
        ALUDIBOND71X24INCH: 174.0,
        CUSTOM10X10: 22.5,
        CUSTOM120X40: 59.0,
        CUSTOM12X12: 27.5,
        CUSTOM150X50: 69.0,
        CUSTOM180X60: 75.0,
        CUSTOM18X24: 42.5,
        CUSTOM20X20: 39.0,
        CUSTOM24X36: 49.0,
        CUSTOM25X25: 22.5,
        CUSTOM30X30: 27.5,
        CUSTOM47X16: 59.0,
        CUSTOM50X50: 39.0,
        CUSTOM50X70: 42.5,
        CUSTOM59X20: 69.0,
        CUSTOM61X91: 49.0,
        CUSTOM70X100: 59.0,
        CUSTOM71X24: 75.0,
        FRAMECLASSIC10X10WOODBLACK: 15.0,
        FRAMECLASSIC25X25WOODBLACK: 15.0,
        FRAMEGLOBAL12X12INCHWOODBLACK: 18.0,
        FRAMEGLOBAL12X12INCHWOODNATURAL: 18.0,
        FRAMEGLOBAL12X12INCHWOODWHITE: 18.0,
        FRAMEGLOBAL18X24INCHWOODBLACK: 35.0,
        FRAMEGLOBAL18X24INCHWOODNATURAL: 35.0,
        FRAMEGLOBAL18X24INCHWOODWHITE: 35.0,
        FRAMEGLOBAL20X20INCHWOODBLACK: 25.0,
        FRAMEGLOBAL20X20INCHWOODNATURAL: 25.0,
        FRAMEGLOBAL20X20INCHWOODWHITE: 25.0,
        FRAMEGLOBAL24X36INCHWOODBLACK: 39.0,
        FRAMEGLOBAL24X36INCHWOODNATURAL: 48.0,
        FRAMEGLOBAL24X36INCHWOODWHITE: 48.0,
        FRAMEGLOBAL30X30CMWOODBLACK: 18.0,
        FRAMEGLOBAL30X30CMWOODNATURAL: 18.0,
        FRAMEGLOBAL30X30CMWOODWHITE: 18.0,
        FRAMEGLOBAL50X50CMWOODBLACK: 27.0,
        FRAMEGLOBAL50X50CMWOODNATURAL: 27.0,
        FRAMEGLOBAL50X50CMWOODWHITE: 27.0,
        FRAMEGLOBAL50X70CMWOODBLACK: 35.0,
        FRAMEGLOBAL50X70CMWOODNATURAL: 35.0,
        FRAMEGLOBAL50X70CMWOODWHITE: 35.0,
        FRAMEGLOBAL61X91CMWOODBLACK: 48.0,
        FRAMEGLOBAL61X91CMWOODNATURAL: 48.0,
        FRAMEGLOBAL61X91CMWOODWHITE: 48.0,
        FRAMEGLOBAL70X100CMWOODBLACK: 52.0,
        FRAMEGLOBAL70X100CMWOODNATURAL: 52.0,
        FRAMEGLOBAL70X100CMWOODWHITE: 52.0,
        FRAMEPANORAMIC120X40CMWOODBLACK: 50.0,
        FRAMEPANORAMIC150X50CMWOODBLACK: 76.0,
        FRAMEPANORAMIC180X60CMWOODBLACK: 124.0,
        FRAMEPANORAMIC47X16INCHWOODBLACK: 50.0,
        FRAMEPANORAMIC59X20INCHWOODBLACK: 76.0,
        FRAMEPANORAMIC71X24INCHWOODBLACK: 124.0
      }
    },
    zoomLevels: [
      { zoom: 0, scaling: 1 },
      { zoom: 1, scaling: 1 },
      { zoom: 5, scaling: 1 / 8 },
      { zoom: 5, scaling: 1 / 4 },
      { zoom: 5, scaling: 1 / 2 },
      { zoom: 5, scaling: 1 },
      { zoom: 6, scaling: 1 },
      { zoom: 7, scaling: 1 },
      { zoom: 8, scaling: 1 },
      { zoom: 11, scaling: 1 / 4 },
      { zoom: 11, scaling: 1 / 2 },
      { zoom: 11, scaling: 1 },
      { zoom: 12, scaling: 1 },
      { zoom: 13, scaling: 1 },
      { zoom: 14, scaling: 1 },
      { zoom: 15, scaling: 1 },
      { zoom: 16, scaling: 1 },
      { zoom: 17, scaling: 1 },
      { zoom: 18, scaling: 1 },
      { zoom: 19, scaling: 1 },
      { zoom: 20, scaling: 1 },
      { zoom: 21, scaling: 1 }
    ],
    orientations: ['portrait', 'landscape', 'square'],
    regions: [
      { name: 'EU', unit: 'cm' },
      { name: 'US', unit: 'in' }
    ],
    mapStyle: {
      classic: createStyle(classicBorders, classicIcons, classicLines, classicTexts),
      ski: createStyle(classicBorders, classicIcons, classicLines, classicTexts),
      twilight: {
        portrait: {
          classic: {
            borders: twilightBorders,
            icons: twilightIcons,
            lines: twilightLines,
            texts: twilightTexts
          },
          simple: {
            borders: twilightBorders,
            icons: twilightIcons,
            texts: twilightTextsSimple
          },
          'canvas-only': {
            icons: twilightIcons
          }
        },
        landscape: {
          classic: {
            borders: twilightBorders,
            icons: twilightIcons,
            lines: twilightLines,
            texts: twilightTexts
          },
          simple: {
            borders: twilightBorders,
            icons: twilightIcons,
            texts: twilightTextsSimple
          },
          'canvas-only': {
            icons: twilightIcons
          }
        },
        square: {
          classic: {
            borders: twilightBorders,
            icons: twilightIcons,
            lines: twilightLines,
            texts: twilightTexts
          },
          simple: {
            borders: twilightBorders,
            icons: twilightIcons,
            texts: twilightTextsSimple
          },
          'canvas-only': {
            icons: twilightIcons
          }
        },
        panoramic: {
          classic: {
            borders: twilightPanoramicBorders,
            icons: twilightIcons,
            lines: twilightLines,
            texts: twilightTexts
          },
          simple: {
            borders: twilightBorders,
            icons: twilightIcons,
            texts: twilightTextsSimple
          },
          'canvas-only': {
            icons: twilightIcons
          }
        }
      },
      'classic-granite': createStyle(
        classicGraniteBorders,
        classicGraniteIcons,
        classicGraniteLines,
        classicGraniteTexts
      ),
      'classic-chrome': createStyle(classicChromeBorders, BlueprintIconsWhite, classicChromeLines, classicChromeTexts),
      'classic-black': createStyle(classicBlackBorders, BlueprintIconsWhite, classicBlackLines, classicBlackTexts),
      'twilight-neon': createStyle(twilightNeonBorders, TwilightNeonIconsWhite, twilightNeonLines, twilightNeonTexts),
      'twilight-red': createStyle(twilightRedBorders, TwilightRedIconsWhite, twilightRedLines, twilightRedTexts),
      'blueprint-no-labels': createStyle(blueprintBorders, BlueprintIconsWhite, blueprintDeepLines, blueprintDeepTexts),
      'blueprint-deep-no-labels': createStyle(
        blueprintDeepBorders,
        BlueprintIconsWhite,
        blueprintDeepLines,
        blueprintDeepTexts
      ),
      'blueprint-purple-no-labels': createStyle(
        blueprintPurpleBorders,
        BlueprintIconsWhite,
        blueprintPurpleLines,
        blueprintPurpleTexts
      ),
      'blueprint-royal-blue-no-labels': createStyle(
        blueprintRoyalBlueBorders,
        BlueprintIconsWhite,
        blueprintRoyalBlueLines,
        blueprintRoyalBlueTexts
      ),
      'blueprint-coral-no-labels': createStyle(
        blueprintCoralBorders,
        BlueprintIconsWhite,
        blueprintCoralLines,
        blueprintCoralTexts
      ),
      'blueprint-white-no-labels': createStyle(
        blueprintWhiteBorders,
        BlueprintIconsWhite,
        blueprintWhiteLines,
        blueprintWhiteTexts
      ),
      'blueprint-surfblue-no-labels': createStyle(
        blueprintSurfblueBorders,
        BlueprintIconsWhite,
        blueprintSurfblueLines,
        blueprintSurfblueTexts
      ),
      'blueprint-olive-no-labels': createStyle(
        blueprintOliveBorders,
        BlueprintIconsWhite,
        blueprintOliveLines,
        blueprintOliveTexts
      ),
      'blueprint-yellow-no-labels': createStyle(
        blueprintYellowBorders,
        BlueprintIconsWhite,
        blueprintYellowLines,
        blueprintYellowTexts
      ),
      'blueprint-dark-no-labels': createStyle(
        blueprintDarkBorders,
        BlueprintIconsWhite,
        blueprintDarkLines,
        blueprintDarkTexts
      ),
      'blueprint-pink-no-labels': createStyle(
        blueprintPinkBorders,
        BlueprintIconsWhite,
        blueprintPinkLines,
        blueprintPinkTexts
      ),
      'blueprint-charcoal-no-labels': createStyle(
        blueprintCharcoalBorders,
        BlueprintIconsWhite,
        blueprintCharcoalLines,
        blueprintCharcoalTexts
      ),
      'blueprint-black-no-labels': createStyle(
        blueprintBlackBorders,
        BlueprintIconsWhite,
        blueprintBlackLines,
        blueprintBlackTexts
      ),
      'blueprint-purple-dark-no-labels': createStyle(
        blueprintPurpleDarkBorders,
        BlueprintIconsWhite,
        blueprintPurpleDarkLines,
        blueprintPurpleDarkTexts
      ),
      'blueprint-cactus-no-labels': createStyle(
        blueprintCactusBorders,
        BlueprintIconsWhite,
        blueprintCactusLines,
        blueprintCactusTexts
      ),
      'blueprint-hazelnut-ice-cream-no-labels': createStyle(
        blueprintHazelnutIceCreamBorders,
        BlueprintIconsHazelnutIceCream,
        blueprintHazelnutIceCreamLines,
        blueprintHazelnutIceCreamTexts
      ),
      'blueprint-green-no-labels': createStyle(
        blueprintGreenBorders,
        BlueprintIconsWhite,
        blueprintGreenLines,
        blueprintGreenTexts
      ),
      'blueprint-chocolate-ice-cream-no-labels': createStyle(
        blueprintChocolateIceCreamBorders,
        BlueprintIconsChocolateIceCream,
        blueprintChocolateIceCreamLines,
        blueprintChocolateIceCreamTexts
      ),
      'blueprint-grapefruit-no-labels': createStyle(
        blueprintGrapefruitBorders,
        BlueprintIconsWhite,
        blueprintGrapefruitLines,
        blueprintGrapefruitTexts
      ),
      'blueprint-orange-no-labels': createStyle(
        blueprintOrangeBorders,
        BlueprintIconsWhite,
        blueprintOrangeLines,
        blueprintOrangeTexts
      ),
      'blueprint-red-no-labels': createStyle(
        blueprintRedBorders,
        BlueprintIconsWhite,
        blueprintRedLines,
        blueprintRedTexts
      )
    }
  }
};



// WEBPACK FOOTER //
// /vercel/path0/src/environment.base.ts